import type { FileType } from '@disco/constants/FileTypes'
import {
  AUDIO_FILE,
  DRM_FILE,
  IMAGE_FILE,
  PDF_FILE,
  SPREADSHEET_FILE,
  TEXT_FILE,
  UNKNOWN_FILE,
  VIDEO_FILE,
} from '@disco/constants/FileTypes'

const videoTypes = [
  'm4v',
  'webm',
  'ogv',
  'mp4',
  'mov',
  'flv',
  'wmv',
  '3gp',
  'mpeg',
  'mpg',
  'mpl',
  'qt',
  'avi',
  'mkv',
]
const audioTypes = [
  'mp3',
  'm4a',
  'ogg',
  'aac',
  'wav',
  'wma',
  'aiff',
  'aif',
  'flac',
  'mpga',
  'caf',
  'mp2',
  'sd2',
]
const textTypes = ['doc', 'docx', 'txt', 'rtf']
const pdfTypes = ['pdf']
const imageTypes = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'tif']
const spreadsheetTypes = ['csv', 'xls', 'xlsx']
const drmTypes = ['m4p']

/**
 * Array of file extensions considered optimized formats.
 *
 * These formats are typically smaller in size and more web-friendly
 * compared to their original counterparts (e.g., MP3 vs WAV).
 *
 * @example
 * ```typescript
 * optimizedTypes.includes('mp3') // true
 * optimizedTypes.includes('wav') // false
 *
 * // Usage in format checking
 * const isOptimized = optimizedTypes.includes(fileExtension)
 * ```
 */
export const optimizedTypes = ['mp3', 'm4a', 'mp4']

/**
 * Gets the MIME type for a video file based on its extension.
 *
 * @param ext - The file extension (with or without dot)
 * @returns The corresponding video MIME type, defaults to 'video/mp4' for unknown extensions
 *
 * @example
 * ```typescript
 * getVideoMediaType('mp4') // returns 'video/mp4'
 * getVideoMediaType('webm') // returns 'video/webm'
 * getVideoMediaType('unknown') // returns 'video/mp4'
 * ```
 */
export function getVideoMediaType(ext?: string | null): string {
  switch ((ext ?? '').toLowerCase()) {
    case 'm4v':
      return 'video/mp4'
    case 'webm':
      return 'video/webm'
    case 'ogv':
      return 'video/ogv'
    case 'mp4':
      return 'video/mp4'
    case 'mov':
      return 'video/quicktime'
    case 'flv':
      return 'video/flv'
    case 'wmv':
      return 'video/wmv'
    case 'mpeg':
      return 'video/mpeg'
    case '3gp':
      return 'video/3gp'
    default:
      return 'video/mp4'
  }
}

/**
 * Gets the MIME type for an audio file based on its extension.
 *
 * @param ext - The file extension (with or without dot)
 * @returns The corresponding audio MIME type, defaults to 'audio/mpeg' for unknown extensions
 *
 * @example
 * ```typescript
 * getAudioMediaType('mp3') // returns 'audio/mpeg'
 * getAudioMediaType('flac') // returns 'audio/flac'
 * getAudioMediaType('unknown') // returns 'audio/mpeg'
 * ```
 */
export function getAudioMediaType(ext?: string | null): string {
  switch ((ext ?? '').toLowerCase()) {
    case 'mp3':
      return 'audio/mpeg'
    case 'm4a':
      return 'audio/mp4'
    case 'ogg':
      return 'audio/ogg'
    case 'aac':
      return 'audio/aac'
    case 'wav':
      return 'audio/wav'
    case 'wma':
      return 'audio/wma'
    case 'aif':
    case 'aiff':
      return 'audio/aiff'
    case 'flac':
      return 'audio/flac'

    default:
      return 'audio/mpeg'
  }
}

/**
 * Gets the MIME type for a document file based on its extension.
 *
 * @param ext - The file extension (with or without dot)
 * @returns The corresponding doc MIME type, defaults to 'application/octet-stream' for unknown extensions
 *
 * @example
 * ```typescript
 * getDocMediaType('pdf') // returns 'application/pdf'
 * getDocMediaType('txt') // returns 'text/plain'
 * getDocMediaType('unknown') // returns 'application/octet-stream'
 * ```
 */
export function getDocMediaType(ext?: string | null): string {
  switch ((ext ?? '').toLowerCase()) {
    case 'pdf':
      return 'application/pdf'
    case 'doc':
      return 'application/msword'
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    case 'txt':
      return 'text/plain'
    case 'rtf':
      return 'application/rtf'
    case 'csv':
      return 'text/csv'
    case 'xls':
      return 'application/vnd.ms-excel'
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    case 'ppt':
      return 'application/vnd.ms-powerpoint'
    case 'pptx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    case 'odp':
      return 'application/vnd.oasis.opendocument.presentation'
    case 'zip':
      return 'application/zip'
    case 'rar':
      return 'application/vnd.rar'
    case '7z':
      return 'application/x-7z-compressed'
    case 'tar':
      return 'application/x-tar'
    case 'gz':
      return 'application/gzip'
    case 'bz2':
      return 'application/x-bzip2'
    default:
      return 'application/octet-stream'
  }
}

/**
 * Checks if a file extension is AIFF format.
 *
 * @param ext - The file extension to check
 * @returns True if the extension is 'aif' or 'aiff'
 *
 * @example
 * ```typescript
 * isAiff('aif') // returns true
 * isAiff('aiff') // returns true
 * isAiff('mp3') // returns false
 * ```
 */
export function isAiff(ext?: string | null): boolean {
  return ext === 'aif' || ext === 'aiff'
}

/**
 * Extracts the file extension from a filename.
 *
 * @param filename - The filename to extract extension from
 * @returns The file extension without the dot, or empty string if none found
 *
 * @example
 * ```typescript
 * getExtension('song.mp3') // returns 'mp3'
 * getExtension('document.PDF') // returns 'pdf'
 * getExtension('noextension') // returns ''
 * ```
 */
export function getExtension(filename: string): string {
  if (!filename) return ''

  const extMatch = /\.([0-9a-z]+)$/i.exec(filename.toLowerCase())
  if (extMatch) {
    const ext = extMatch[1] || ''
    return ext
  }

  return ''
}

/**
 * Extracts the file extension from a URI/URL.
 *
 * Handles URLs with query parameters by stopping at '?' character.
 *
 * @param uri - The URI/URL to extract extension from
 * @returns The file extension without the dot, or empty string if none found
 *
 * @example
 * ```typescript
 * getExtensionFromUri('https://example.com/file.mp3') // returns 'mp3'
 * getExtensionFromUri('https://example.com/file.mp3?version=1') // returns 'mp3'
 * getExtensionFromUri('https://example.com/noext') // returns ''
 * ```
 */
export function getExtensionFromUri(uri?: string | null): string {
  if (!uri) return ''

  const extMatch = /\.([0-9a-z]+)($|\?)/i.exec(uri.toLowerCase())
  if (extMatch) {
    const ext = extMatch[1] || ''
    return ext
  }

  return ''
}

/**
 * Determines the file type category based on file extension.
 *
 * @param ext - The file extension to categorize
 * @returns The FileType constant representing the category
 *
 * @example
 * ```typescript
 * getFileTypeByExt('mp3') // returns AUDIO_FILE
 * getFileTypeByExt('mp4') // returns VIDEO_FILE
 * getFileTypeByExt('pdf') // returns PDF_FILE
 * getFileTypeByExt('unknown') // returns UNKNOWN_FILE
 * ```
 */
export function getFileTypeByExt(ext?: string | null): FileType {
  const lowercasedExt = ext ? ext.toLowerCase() : ''
  if (drmTypes.indexOf(lowercasedExt) > -1) return DRM_FILE
  if (videoTypes.indexOf(lowercasedExt) > -1) return VIDEO_FILE
  if (audioTypes.indexOf(lowercasedExt) > -1) return AUDIO_FILE
  if (textTypes.indexOf(lowercasedExt) > -1) return TEXT_FILE
  if (pdfTypes.indexOf(lowercasedExt) > -1) return PDF_FILE
  if (imageTypes.indexOf(lowercasedExt) > -1) return IMAGE_FILE
  if (spreadsheetTypes.indexOf(lowercasedExt) > -1) return SPREADSHEET_FILE
  return UNKNOWN_FILE
}

/**
 * Determines the MIME type for a file based on its extension.
 *
 * @param ext - The file extension (with or without dot)
 * @returns The corresponding MIME type string
 *
 * @example
 * ```typescript
 * getMimeTypeByExt('mp3') // returns 'audio/mpeg'
 * getMimeTypeByExt('mp4') // returns 'video/mp4'
 * getMimeTypeByExt('jpg') // returns 'image/jpg'
 * getMimeTypeByExt('pdf') // returns 'application/pdf'
 * getMimeTypeByExt('unknown') // returns default 'application/octet-stream' for doc types
 * ```
 */
export function getMimeTypeByExt(ext?: string | null): string {
  let mimeType = ''

  if (!ext) return mimeType

  const fileType = getFileTypeByExt(ext)
  if (fileType === AUDIO_FILE) {
    mimeType = getAudioMediaType(ext)
  } else if (fileType === VIDEO_FILE) {
    mimeType = getVideoMediaType(ext)
  } else if (fileType === IMAGE_FILE) {
    mimeType = `image/${ext.toLowerCase()}`
  } else {
    mimeType = getDocMediaType(ext)
  }
  return mimeType
}

/**
 * Gets an appropriate noun to describe a file based on its extension.
 *
 * @param ext - The file extension to get noun for
 * @returns 'track' for audio files, 'video' for video files, 'file' for others
 *
 * @example
 * ```typescript
 * getNounForExt('mp3') // returns 'track'
 * getNounForExt('mp4') // returns 'video'
 * getNounForExt('pdf') // returns 'file'
 * ```
 */
export function getNounForExt(ext?: string | null): string {
  const fileType: FileType = getFileTypeByExt(ext)
  if (fileType === AUDIO_FILE) return 'track'
  if (fileType === VIDEO_FILE) return 'video'
  else return 'file'
  // consider enhancing 'file' with more verbose types: 'document', 'image'
}

/**
 * Checks if a file format can be optimized (transcoded to a more efficient format).
 *
 * Returns true for formats that are not already in optimized formats (mp3, m4a, mp4).
 *
 * @param ext - The file extension to check
 * @returns True if the format can be optimized, false if already optimized
 *
 * @example
 * ```typescript
 * canFormatBeOptimised('wav') // returns true (can be optimized to mp3)
 * canFormatBeOptimised('mp3') // returns false (already optimized)
 * canFormatBeOptimised('flac') // returns true (can be optimized)
 * ```
 */
export function canFormatBeOptimised(ext: string): boolean {
  return !optimizedTypes.includes(ext.toLowerCase())
}

/**
 * Determines if a file format belongs in the optimized zip download.
 *
 * Optimized formats (mp3, m4a, mp4) and documents go in optimized zip,
 * while original formats (wav, flac, etc.) go in original zip.
 *
 * @param ext - The file extension to check
 * @returns True if format belongs in optimized zip, false for original zip
 *
 * @example
 * ```typescript
 * isFormatBelongsToOptimizedZip('mp3') // returns true (optimized format)
 * isFormatBelongsToOptimizedZip('pdf') // returns true (document)
 * isFormatBelongsToOptimizedZip('wav') // returns false (original format)
 * ```
 */
export function isFormatBelongsToOptimizedZip(ext: string): boolean {
  // if true - format goes in `zip_stream/?mode=optimized e.g. mp3, pdf
  // if false - format goes in `zip_stream/?mode=original e.g. wav
  const isOptimized = !canFormatBeOptimised(ext)
  const type = getFileTypeByExt(ext)
  const isDocument = type !== AUDIO_FILE && type !== VIDEO_FILE
  return isOptimized || isDocument
}

/**
 * Checks if a URL is an HLS (HTTP Live Streaming) stream URL.
 *
 * @param url - The URL to check
 * @returns True if the URL contains 'out.m3u8', false otherwise
 *
 * @example
 * ```typescript
 * isHlsStreamUrl('https://example.com/stream/out.m3u8') // returns true
 * isHlsStreamUrl('https://example.com/video.mp4') // returns false
 * ```
 */
export const isHlsStreamUrl = (url?: string | null): boolean =>
  url?.includes('out.m3u8') || false

/**
 * Formats a file size in bytes to a human-readable string.
 *
 * @param bytes - The size in bytes
 * @param decimals - Number of decimal places to show (default: 2)
 * @returns Formatted file size string with appropriate unit
 *
 * @example
 * ```typescript
 * formatFileSize(1024) // returns '1.00 KB'
 * formatFileSize(1048576) // returns '1.00 MB'
 * formatFileSize(0) // returns '0 Bytes'
 * formatFileSize(1536, 1) // returns '1.5 KB'
 * ```
 */
export const formatFileSize = (bytes: number, decimals: number = 2): string => {
  if (!+bytes) return '0 Bytes'

  const k = 1024
  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const unitIndex = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, unitIndex)).toFixed(decimals))} ${
    units[unitIndex]
  }`
}

/**
 * Checks if a filename represents a hidden system file.
 *
 * Identifies files that are typically hidden in the filesystem and not meant to be
 * directly managed by users, such as .DS_Store, .gitignore or thumbs.db files.
 *
 * @param filename - The filename to check
 * @returns True if the file is hidden (starts with .) or is thumbs.db
 *
 * @example
 * ```typescript
 * isHiddenSystemFile('.DS_Store') // returns true
 * isHiddenSystemFile('thumbs.db') // returns true
 * isHiddenSystemFile('song.mp3') // returns false
 * ```
 */
const isHiddenSystemFile = (filename: string): boolean => {
  const lowerFilename = filename.toLowerCase()
  return lowerFilename.startsWith('.') || lowerFilename === 'thumbs.db'
}

/**
 * Filters out hidden system files from an array of File objects.
 *
 * Removes files that start with '.' or are named 'thumbs.db' (case-insensitive).
 *
 * @param files - Array of File objects to filter
 * @returns Filtered array with hidden system files removed
 *
 * @example
 * ```typescript
 * const files = [
 *   new File([''], 'song.mp3'),
 *   new File([''], '.DS_Store'),
 *   new File([''], 'Thumbs.db')
 * ]
 * omitHiddenSystemFiles(files) // returns [File('song.mp3')]
 * ```
 */
export const omitHiddenSystemFiles = (files: File[]): File[] =>
  files.filter(file => !isHiddenSystemFile(file.name))

type DirectoryReader = {
  readEntries: (resolve: any, reject: any) => void
}

type FileSystemEntry = {
  isDirectory: boolean
  isFile: boolean
  name: string
  file: (resolve: (file: File) => void, reject: (error: Error) => void) => void
}

type FileSystemDirectoryEntry = FileSystemEntry & {
  isDirectory: true
  isFile: false
  createReader: () => DirectoryReader
}

/**
 * Processes a file system entry and extracts File objects.
 *
 * Recursively handles both file and directory entries, skipping hidden system files.
 * For file entries, returns a single-item array with the File object.
 * For directory entries, recursively processes all contained files.
 *
 * @param entry - FileSystemEntry to process (file or directory)
 * @returns Promise resolving to array of File objects
 *
 * @example
 * ```typescript
 * // Process a file entry
 * const fileEntry = getFileEntry('document.pdf');
 * const files = await processEntry(fileEntry);
 * // Returns [File] with a single File object
 *
 * // Process a directory entry
 * const dirEntry = getDirectoryEntry('photos');
 * const files = await processEntry(dirEntry);
 * // Returns [File, File, ...] with multiple File objects from directory
 * ```
 */
const processEntry = async (entry: FileSystemEntry): Promise<File[]> => {
  // Skip hidden system files
  if (isHiddenSystemFile(entry.name)) return []

  // Process directory entries recursively
  if (entry.isDirectory)
    return getFilesFromDirectory(entry as FileSystemDirectoryEntry)

  // It's a file entry so read the file
  const file = await new Promise<File>(
    (
      resolve: (result: Promise<File> | File) => void,
      reject: (error?: any) => void,
    ) => entry.file(resolve, reject),
  )
  return [file]
}

/**
 * Recursively reads all files from a directory.
 *
 * Uses the FileSystem API's directory reader to extract all files within a directory,
 * including those in nested subdirectories. Handles pagination of directory entries.
 *
 * @param directory - FileSystemDirectoryEntry to read files from
 * @returns Promise resolving to array of all File objects in the directory tree
 *
 * @example
 * ```typescript
 * // Get all files from a dropped folder
 * const dirEntry = event.dataTransfer.items[0].webkitGetAsEntry();
 * if (dirEntry.isDirectory) {
 *   const allFiles = await getFilesFromDirectory(dirEntry);
 *   console.log(`Found ${allFiles.length} files in directory tree`);
 * }
 * ```
 */
const getFilesFromDirectory = async (
  directory: FileSystemDirectoryEntry,
): Promise<File[]> => {
  const reader = directory.createReader()
  let files: File[] = []

  try {
    let entries: FileSystemEntry[]
    // eslint-disable-next-line no-constant-condition
    while (true) {
      entries = await new Promise<FileSystemEntry[]>(
        (
          resolve: (
            result: Promise<Array<FileSystemEntry>> | Array<FileSystemEntry>,
          ) => void,
          reject: (error?: any) => void,
        ) => reader.readEntries(resolve, reject),
      )

      // Exit loop when no more entries are found
      if (entries.length === 0) break

      // Process each entry and accumulate results
      const results = await Promise.all(entries.map(processEntry))
      files = files.concat(results.flat()) // Process each entry and accumulate results
    }
  } catch (error: any) {
    console.error('Failed to read directory entries:', error)
    throw error // Re-throw after logging
  }

  return files
}

/**
 * Extracts files from a drag and drop operation, including files within directories.
 *
 * Recursively processes directories to extract all files, while filtering out
 * hidden system files. Handles both individual files and directory structures.
 *
 * @param draggedItem - The drag event data containing items
 * @returns Promise resolving to array of File objects from the drag operation
 *
 * @example
 * ```typescript
 * const handleDrop = async (event) => {
 *   event.preventDefault()
 *   const files = await getFilesFromDraggedItem(event.dataTransfer)
 *   console.log(`Dropped ${files.length} files`)
 * }
 * ```
 */
export const getFilesFromDraggedItem = async (draggedItem: {
  items: any
}): Promise<File[]> => {
  const items = Array.from(draggedItem.items).map((item: any) =>
    item.webkitGetAsEntry ? item.webkitGetAsEntry() : item,
  )

  try {
    const files = await Promise.all(items.map(processEntry))
    return omitHiddenSystemFiles(files.flat())
  } catch (error: any) {
    console.error('Error processing files and folders:', error)
    return []
  }
}
